// @flow

import _ from 'lodash';
import moment from 'moment';

import { Spreadsheet } from '../models/spreadsheet';
import translate from '../scripts/core/tools/translate';
import { notify, NOTIFY_TYPE } from '../scripts/shared/notify';

export class RetentionSpreadsheet extends Spreadsheet {
    name: string = `${translate('Report.RetentionDataExtract').toASCII().toLowerCase().toUnderscoreCase()}`;

    constructor (noFilter: boolean = false) {
        super();

        this.noFilter = noFilter;
    }

    mount (dataSource: any) {
        const sectionColumns: any = {
            vehicle: [
                { key: 'chassi', text: 'CHASSI', textAlign: 'center' },
                { key: 'family', text: 'VEÍCULO', textAlign: 'center' },
                { key: 'dealerNumber', text: 'DN', textAlign: 'center' },
                { key: 'dealerName', text: 'DEALER' },
                { key: 'salesDate', text: 'DATA VENDA VEÍCULO', textAlign: 'center' }
            ],
            revision: [
                { key: 'revisionKM', text: 'REVISAO PROGRAMADA', textAlign: 'center' },
                { key: 'type', text: 'TIPO RETORNO', textAlign: 'center' },
                { key: 'lastRevisionDate', text: 'DATA REVISAO', textAlign: 'center' },
                { key: 'vehicleKM', text: 'KM VEICULO', textAlign: 'center' },
                { key: 'serviceOrder', text: 'OS', textAlign: 'center' }
            ],
            person: [
                { key: 'name', text: 'NOME CLIENTE ATUAL' },
                { key: 'ssn', text: 'CPF CLIENTE ATUAL' },
                { key: 'gender', text: 'GENERO CLIENTE ATUAL' },
                { key: 'phoneNumber', text: 'TELEFONE CLIENTE ATUAL', textAlign: 'center' },
                { key: 'mobileNumber', text: 'CELULAR CLIENTE ATUAL', textAlign: 'center' },
                { key: 'email', text: 'EMAIL CLIENTE ATUAL' },
                { key: 'status', text: 'CEP OBSERVACAO', textAlign: 'center' },
                { key: 'zip', text: 'CEP CLIENTE ATUAL', textAlign: 'center' },
                { key: 'street', text: 'LOGRADOURO CLIENTE ATUAL' },
                { key: 'district', text: 'BAIRRO CLIENTE ATUAL' },
                { key: 'city', text: 'CIDADE CLIENTE ATUAL' },
                { key: 'state', text: 'UF CLIENTE ATUAL', textAlign: 'center' }
            ]
        };

        const sections: Array<any> = [
            { key: 'vehicle', text: 'INFORMACOES DA VENDA DO VEÍCULO', index: 0 },
            { key: 'revision', text: `INFORMACOES DA REVISÃO DO VEÍCULO`, index: sectionColumns.vehicle.length },
            {
                key: 'person',
                text: 'INFORMACOES DO CLIENTE',
                index: sectionColumns.vehicle.length + sectionColumns.revision.length
            }
        ];

        const baseStyle: any = {
            textAlign: 'left',
            fontSize: 11,
            verticalAlign: 'center',
            fontFamily: 'Calibri',
            borderRight: {
                color: '#DDD',
                size: 1
            },
            borderBottom: {
                color: '#DDD',
                size: 1
            }
        };

        const baseHeaderStyle: any = {
            color: '#FFFFFF',
            fontSize: 11,
            bold: true,
            textAlign: 'center',
            verticalAlign: 'center',
            fontFamily: 'Calibri'
        };

        const typeOrder = {
            NoRetention: 0,
            BrandSpreading: 1,
            GroupSpreading: 2,
            BrandRetention: 3,
            GroupRetention: 4,
            OwnRetention: 5
        };

        const colorsStyle = {
            OwnRetention: _.assign({}, baseStyle, { background: '#99cbed' }),
            GroupSpreading: _.assign({}, baseStyle, { background: '#cdacda' }),
            GroupRetention: _.assign({}, baseStyle, { background: '#cdacda' }),
            BrandSpreading: _.assign({}, baseStyle, { background: '#f9cd88' }),
            NoRetention: _.assign({}, baseStyle, { background: '#f3a59d' }),
            BrandRetention: _.assign({}, baseStyle, { background: '#8cddcd' }),
            InvalidPosition: _.assign({}, baseStyle, { background: '#FFCDD2' })
        };

        const getCellColor = (data, key) => {
            if (key === 'status' && data['status'] === 0) {
                return colorsStyle['InvalidPosition'];
            }
            return colorsStyle[data['type']];
        };

        const baseColorStyle: any = {
            vehicle: '#9E9E9E',
            revision: '#616161',
            person: '#212121'
        };

        const baseBorderColorStyle: any = {
            vehicle: '#eee',
            revision: '#eee',
            person: '#eee'
        };

        const headerStyle = _.reduce(
            sections,
            (acc: any, section: any) => {
                acc[section.key] = _.assign({}, baseHeaderStyle, { background: baseColorStyle[section.key] });
                return acc;
            },
            {}
        );

        const dealerName = (_.find(dataSource, d => d.type === 'OwnRetention') || { dealerName: '' }).dealerName;

        this.name = `${this.name}_${dealerName.toASCII().toLowerCase().toUnderscoreCase()}`;

        const data = _(dataSource)
            .filter(d => this.noFilter || _.some(['OwnRetention', 'BrandSpreading', 'GroupSpreading', 'NoRetention'], t => d.type === t))
            .orderBy('chassi')
            .orderBy(d => typeOrder[d.type])
            .orderBy('revisionKM')
            .groupBy(d => moment(d.revisionDate).endOf('month').format('YYYYMM'))
            .value();

        if (!_.size(data)) {
            notify(NOTIFY_TYPE.LOG, 'Sem dados de retenção para a loja selecionada.');
            return this;
        }

        this.workbookOptions = {
            sheets: _.map(data, (data, date) => ({
                name: moment(date, 'YYYYMM').endOf('month').format('MMM | YYYY') + (moment(date, 'YYYYMM').startOf('month').toDate() >= moment().startOf('month').toDate() ? ' - Parcial' : ''),
                frozenColumns: 2,
                frozenRows: 2,
                filter: {
                    ref: `A2:${this.columnIndex[sections[2].index + sectionColumns.person.length - 2]}${data.length + 2}`,
                    columns: []
                },
                rows: [
                    {
                        cells: [
                            ..._.map(sections, (section: any) =>
                                _.assign({}, headerStyle[section.key], {
                                    value: section.text,
                                    index: section.index,
                                    borderLeft: { color: baseBorderColorStyle[section.key], size: 1 },
                                    borderRight: { color: baseBorderColorStyle[section.key], size: 1 }
                                })
                            )
                        ]
                    },
                    {
                        cells: [
                            ..._(sectionColumns)
                                .map((columns: any, key: any) =>
                                    _.map(columns, (cell: any) =>
                                        _.assign({}, headerStyle[key], {
                                            value: cell.text,
                                            wrap: true,
                                            borderLeft: { color: baseBorderColorStyle[key], size: 1 },
                                            borderRight: { color: baseBorderColorStyle[key], size: 1 },
                                            borderTop: { color: baseBorderColorStyle[key], size: 1 },
                                            borderBottom: { color: baseBorderColorStyle[key], size: 1 }
                                        })
                                    )
                                )
                                .flatten()
                                .value()
                        ]
                    },
                    ..._.map(data, d => {
                        return {
                            cells: [
                                ..._(sectionColumns)
                                    .map((columns: any, key: any) =>
                                        _.map(columns, (cell: any) =>
                                            _.assign({}, getCellColor(d, cell.key), {
                                                value: (() => {
                                                    switch (cell.key) {
                                                    case 'salesDate':
                                                    case 'lastRevisionDate':
                                                    case 'revisionDate':
                                                        return d[cell.key]
                                                            ? moment(d[cell.key]).endOf('day').toDate()
                                                            : null;
                                                    case 'status':
                                                        return d[cell.key] === 1
                                                            ? ''
                                                            : translate('Common.VerifyPostalCode');
                                                    case 'type':
                                                        return translate(`Retention.Classification.${d[cell.key]}`);
                                                    default:
                                                        return d[cell.key];
                                                    }
                                                })(),
                                                textAlign: cell.textAlign
                                            })
                                        )
                                    )
                                    .flatten()
                                    .value()
                            ]
                        };
                    })
                ],
                columns: [
                    {
                        index: 0,
                        width: 200
                    },
                    {
                        index: 1,
                        width: 140
                    },
                    {
                        index: 2,
                        width: 140
                    },
                    {
                        index: 3,
                        width: 140
                    },
                    {
                        index: 4,
                        width: 140
                    },
                    {
                        index: 5,
                        width: 140
                    },
                    {
                        index: 6,
                        width: 240
                    },
                    {
                        index: 7,
                        width: 140
                    },
                    {
                        index: 8,
                        width: 140
                    },
                    {
                        index: 9,
                        width: 140
                    },
                    {
                        index: 10,
                        width: 240
                    },
                    {
                        index: 11,
                        width: 140
                    },
                    {
                        index: 12,
                        width: 140
                    },
                    {
                        index: 13,
                        width: 140
                    },
                    {
                        index: 14,
                        width: 140
                    },
                    {
                        index: 15,
                        width: 240
                    },
                    {
                        index: 16,
                        width: 140
                    },
                    {
                        index: 17,
                        width: 140
                    },
                    {
                        index: 18,
                        width: 240
                    },
                    {
                        index: 19,
                        width: 140
                    },
                    {
                        index: 20,
                        width: 140
                    },
                    {
                        index: 21,
                        width: 140
                    },
                    {
                        index: 22,
                        width: 140
                    }
                ],
                showGridLines: true,
                mergedCells: [
                    `A1:${this.columnIndex[sections[1].index - 1]}1`,
                    `${this.columnIndex[sections[1].index]}1:${this.columnIndex[sections[2].index - 1]}1`,
                    `${this.columnIndex[sections[2].index]}1:${this.columnIndex[sections[2].index + sectionColumns.person.length - 1]}1`
                ]
            }))
        };

        return this;
    }
}



// WEBPACK FOOTER //
// ./src/spreadsheets/retention-spreadsheet.js